import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, takeReturn } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PinForm } from "@/components/PinForm";
import { LegalLinks } from "@/components/LegalPage";
import { NewCellarForm, CellarModeTabs } from "@/components/NewCellarForm";

const HERO = "/cellar-hero.jpg";

export const EntryShell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0D0A0B]">
    <div className="hidden lg:block relative">
      <img src={HERO} alt="Wine cellar" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0A0B]/30 to-[#0D0A0B]" />
      <p className="absolute bottom-12 left-12 font-serif text-4xl max-w-md text-[#F5F0EB] leading-tight">Every bottle, every box, always in its place.</p>
    </div>
    <div className="flex flex-col justify-center px-6 sm:px-16 py-12 max-w-xl w-full rise">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Private cellar</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F0EB] mb-10">The Cellar Book</h1>
      {children}
      <LegalLinks className="mt-12" />
    </div>
  </div>
);

const OwnerLogin = ({ onBack }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.dismiss();
      navigate("/choose-cave");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col" data-testid="login-form">
      <label className="text-xs uppercase tracking-widest text-[#6E665D] mb-2">Email</label>
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 mb-6 bg-[#161214]" data-testid="login-email-input" />
      <label className="text-xs uppercase tracking-widest text-[#6E665D] mb-2">Password</label>
      <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 mb-8 bg-[#161214]" data-testid="login-password-input" />
      <Button type="submit" disabled={busy} className="h-12 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB] text-base" data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
      <button type="button" onClick={onBack} className="mt-6 text-sm text-[#A89F91] hover:text-[#D4AF37]" data-testid="back-to-code-button">Use a cellar code instead</button>
    </form>
  );
};

export default function Entry() {
  const navigate = useNavigate();
  const [caves, setCaves] = useState([]);
  const [caveId, setCaveId] = useState("");
  const [owner, setOwner] = useState(false);
  const [mode, setMode] = useState("existing");

  useEffect(() => {
    api.get("/caves/public").then((r) => {
      setCaves(r.data);
      if (r.data.length === 1) setCaveId(r.data[0].id);
    });
  }, []);

  return (
    <EntryShell>
      {owner ? <OwnerLogin onBack={() => setOwner(false)} /> : (
        <>
          <CellarModeTabs mode={mode} setMode={setMode} />
          {mode === "new" ? <NewCellarForm needLogin /> : (
        <>
          <label className="text-xs uppercase tracking-widest text-[#6E665D] mb-2 block">Cellar</label>
          <Select value={caveId || undefined} onValueChange={setCaveId}>
            <SelectTrigger className="h-12 mb-5 bg-[#161214]" data-testid="cave-select"><SelectValue placeholder="Choose a cellar" /></SelectTrigger>
            <SelectContent>
              {caves.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`cave-option-${c.id}`}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <PinForm caveId={caveId} onDone={() => navigate(takeReturn())} />
          <button onClick={() => setOwner(true)} className="mt-8 text-sm text-[#6E665D] hover:text-[#D4AF37]" data-testid="owner-login-link">Owner sign in</button>
        </>
          )}
        </>
      )}
    </EntryShell>
  );
}
